import { AccountContext, AccountPermissions } from 'app/core/lib/account-context';
import { CustomElementBase } from 'app/shared/elements/custom-element-base';
import { EssContextMenuCustomElement } from 'app/shared/elements/ess-context-menu';
import { EssModalCustomElement } from 'app/shared/elements/ess-dialog';
import { CompassFlags } from 'app/shared/flags/compass-flags';
import { animateOnce } from 'app/shared/lib/dom';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { deepClone } from 'app/shared/lib/utilities';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { GridConfig, GridConfigurationService, ICellConfig } from 'app/shared/services/grid-configuration-service';
import { autoinject, BehaviorInstruction, bindable, bindingMode, observable, processContent, TargetInstruction, View } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { getKey, Key } from 'app/shared/lib/keys';
import { guid } from 'app/shared/lib/guid';
import { EventAggregator } from 'aurelia-event-aggregator';
import { DynamicGridEditLayoutPreviewEvent, DynamicGridReloadEvent } from 'app/shared/lib/events';

const DEFAULT_CELL_HEIGHT = 1;
const DEFAULT_CELL_WIDTH = 3;
const MAX_GRID_COLUMNS = 12;
const COMPONENT_TEMPLATE_MAP = new WeakMap<BehaviorInstruction, DocumentFragment>();

const DynamicGridSelector = {
    Cell: '.ess-dynamic-grid-cell',
    ResizableContainer: '.ess-dynamic-grid-cell-handle-container'
};

export interface IComponentConfig {
    key: string,
    name: string,
    height: number,
    width: number,
    content: Element
}

interface IResizeContainer {
    h0: number,
    w0: number,
    x0: number,
    y0: number,
    hShift: number,
    wShift: number,
    yShift: number,
    xShift: number,
    resizeDirectionFlag: CompassFlags,
    element: HTMLElement,
    componentIndex: number
}

interface IShiftContainer {
    x0: number,
    y0: number,
    xShift: number,
    yShift: number,
    element: HTMLElement,
    componentIndex: number
}

enum DynamicGridEditMode {
    Off = 0,
    Config,
    Template,
}

// Attributes of live preview components that need to be modified when changing the state of DynamicGridEditMode
interface IFocusableAttributes {
    tabindex: string | null;
}

function findOverlappingCells(cell: ICellConfig, others: ICellConfig[]) {
    return others.filter((other: ICellConfig) => isCellOverlapping(cell, other));
}

function hasOverlappingCells(cell: ICellConfig, others: ICellConfig[]) {
    return others.some((other: ICellConfig) => isCellOverlapping(cell, other));
}

function isCellOverlapping(cell: ICellConfig, other: ICellConfig) {
    return other !== cell
        && other.top < cell.top + cell.height
        && other.left + other.width > cell.left
        && other.top + other.height > cell.top
        && other.left < cell.left + cell.width;
}

type CellPosition = { top: number, left: number };

@autoinject()
@processContent(EssDynamicGridCustomElement.processContent)
export class EssDynamicGridCustomElement extends CustomElementBase {

    @bindable() protected key: string = '';
    @bindable() protected rows = 1;
    @bindable({ defaultBindingMode: bindingMode.toView })
    protected label: string = '';  //Used for labelling the overall component, buttons, and menus

    @observable() protected gridConfig: GridConfig;
    protected defaultConfig: GridConfig;
    protected components: IComponentConfig[];
    protected customComponents!: IComponentConfig[];
    protected parentView!: View;
    protected editMode: DynamicGridEditMode = DynamicGridEditMode.Off;
    protected isAdmin = false;
    protected focusableElementMap = new Map<Element, IFocusableAttributes>();

    /**
     * The cell clicked for the context menu. Used to determine where a new component should be added.
     */
    protected activeCell: CellPosition | null = null;
    protected focusCell: CellPosition | null = null;
    protected resize: IResizeContainer | null = null;
    protected shift: IShiftContainer | null = null;
    protected colGap!: number;
    protected rowGap!: number;
    protected dRow!: number;
    protected dCol!: number;

    protected CompassFlags = CompassFlags;
    protected DynamicGridEditMode = DynamicGridEditMode;

    // refs
    protected $addComponentModal!: EssModalCustomElement;
    protected $cellContextMenu!: EssContextMenuCustomElement;
    protected $gridContextMenu!: EssContextMenuCustomElement;

    protected id: guid = guid();

    constructor(element: Element, appLogger: AppLogger, { behaviorInstructions: [instruction] }: TargetInstruction,
        protected readonly gridConfigService: GridConfigurationService, protected readonly account: AccountContext, protected readonly env: EnvironmentService, protected readonly i18n: I18N,
        private readonly events: EventAggregator) {
        super(element, appLogger);
        const template = COMPONENT_TEMPLATE_MAP.get(instruction)!;
        const config: ICellConfig[] = this.defaultConfig = this.gridConfig = [];
        const components: IComponentConfig[] = this.components = [];
        Array.from<Element>(template.children).map((component) => {
            const key = component.getAttribute('key') || '';
            const name = component.getAttribute('name') || key;
            const top = parseInt(component.getAttribute('top') || '') || undefined;
            const left = parseInt(component.getAttribute('left') || '') || undefined;
            const height = parseInt(component.getAttribute('height') || '') || DEFAULT_CELL_HEIGHT;
            const width = parseInt(component.getAttribute('width') || '') || DEFAULT_CELL_WIDTH;
            const hidden = Array.from(component.attributes).find((attr) => /default/i.test(attr.name))?.value === 'false';
            const element = component.cloneNode(true) as Element;
            if(hidden === false) {
                config.push({
                    key,
                    top: top!,
                    left: left!,
                    height,
                    width
                });
            }
            components.push({ key, name, height, width, content: element });
        });
    }

    protected tryTranslate(key: string) {
        return /^[a-z]+\:\S+$/i.test(key) ? this.i18n.tr(key) : key;
    }

    protected created(owningView: View) {
        this.parentView = owningView;
    }

    protected bind() {
        if(!this.key) {
            throw 'A dynamic grid was initialized without a key, which disables saving.';
        }
        this.components.push(...this.env.getCustomComponents(this.key));
    }

    protected async attached() {
        const gridConfigPromise = this.gridConfigService.getGridConfiguration(this.key);
        this.computeCellDimensions();
        Array.from(this.element.querySelectorAll(DynamicGridSelector.Cell)).forEach((cell, index) => {
            const config = this.gridConfig[index];
            const { offsetTop, offsetLeft } = cell as HTMLElement;
            if(!config.top) {
                config.top = Math.round(offsetTop / this.dRow) + 1;
            }
            if(!config.left) {
                config.left = Math.round(offsetLeft / this.dCol) + 1;
            }
        });
        this.isAdmin = this.account.hasPermission(AccountPermissions.ApplicationAdmin);
        this.gridConfig = await gridConfigPromise || deepClone(this.defaultConfig);

    }

    // INFO [Matt] If a component is no longer available, remove it from the config.
    // QUESTION [Matt] There's a technical need for this, but as a user I
    // wouldn't want my components randomly disappearing. Can we handle this better?
    protected gridConfigChanged(gridConfig: ICellConfig[]) {
        gridConfig = gridConfig.filter((cell) => this.components.find((c) => c.key === cell.key) !== undefined);
        if(this.gridConfig.length !== gridConfig.length) {
            this.gridConfig = gridConfig;
        }
    }

    /**
     * Opens a context menu programmatically. Also clears the active cell,
     * since there is no active cell when programmatically opening the context menu.
     * @param menu The menu to open.
     * @param event The click event which triggered this method.
     */
    protected openContextMenu(menu: EssContextMenuCustomElement, event: MouseEvent) {
        this.activeCell = null;
        menu.openAtEvent(event);
    }

    /**
     * Tracks which cell was clicked when the context menu was opened. Used to determine where to add new components.
     * @note returns true so the event is not default prevented and the context menu event properly fires
     */
    protected trackGridCell({ offsetX, offsetY, target }: MouseEvent): true {

        // if target isn't the root <ess-dynamic-grid> element
        if(target !== this.element) {
            // check if target is a context menu
            if(target instanceof Element) {
                const contextMenu = target.closest('ess-context-menu');
                // if it's a context menu, stop now
                // don't change `activeCell`
                if(contextMenu != null) {
                    return true;
                }
            }
            // mouse is over an actual component (and not a context menu)
            // so there is no active cell
            // don't bother with calculations
            this.activeCell = null;
            return true;
        };

        this.computeCellDimensions();
        this.focusCell = this.activeCell = {
            top: Math.ceil(offsetY / this.dRow),
            left: Math.ceil(offsetX / this.dCol)
        };
        return true;
    }

    protected addComponent({ key }: IComponentConfig) {
        if(!this.focusCell) { return; }
        const { top, left } = this.focusCell;
        // TODO [Matt] Use default height and width or largest available.
        this.gridConfig.push({ top, left, height: 1, width: 1, key });
        this.focusCell = null;

        // Fire preview event to force reload data for previewed components
        const gridKeys = this.gridConfig.map(cell => cell.key);
        this.events.publish(new DynamicGridEditLayoutPreviewEvent(gridKeys));

        this.$addComponentModal.close();
    }

    protected async resetLayout() {
        this.gridConfig = await this.gridConfigService.getGridTemplateConfiguration(this.key) || deepClone(this.defaultConfig);
        this.saveLayout();
    }

    protected resetLayoutTemplate() {
        this.gridConfig = deepClone(this.defaultConfig);
        this.saveLayoutTemplate();
    }

    protected resetAndApplyLayoutTemplate() {
        this.gridConfig = deepClone(this.defaultConfig);
        this.saveAndApplyLayoutTemplate();
    }

    protected editLayout() {
        this.hideFocusable();
        this.editMode = DynamicGridEditMode.Config;
        setTimeout(() => this.element.querySelector<HTMLElement>('.ess-dynamic-grid-cell-context-menu-button')?.focus(), 250);
    }

    protected async editTemplateLayout() {
        this.gridConfig = await this.gridConfigService.getGridTemplateConfiguration(this.key) || deepClone(this.defaultConfig);
        this.hideFocusable();
        this.editMode = DynamicGridEditMode.Template;
        this.element.focus();
    }

    protected saveLayout() {
        this.gridConfigService.updateGridConfiguration(this.key, this.gridConfig);
        this.revertFocusable();
        this.editMode = DynamicGridEditMode.Off;
        this.events.publish(new DynamicGridReloadEvent());
    }

    protected async saveLayoutTemplate() {
        this.gridConfigService.updateGridTemplateConfiguration(this.key, this.gridConfig);
        const config = await this.gridConfigService.getGridConfiguration(this.key);
        if(config) {
            this.gridConfig = config;
        }
        this.revertFocusable();
        this.editMode = DynamicGridEditMode.Off;
        this.events.publish(new DynamicGridReloadEvent());
    }

    protected saveAndApplyLayoutTemplate() {
        Promise.all([
            this.gridConfigService.updateGridTemplateConfiguration(this.key, this.gridConfig),
            this.gridConfigService.updateGridConfiguration(this.key, this.gridConfig)
        ]);
        this.gridConfig = deepClone(this.gridConfig);
        this.revertFocusable();
        this.editMode = DynamicGridEditMode.Off;
        this.events.publish(new DynamicGridReloadEvent());
    }

    protected async cancelChanges() {
        this.gridConfig = await this.gridConfigService.getGridConfiguration(this.key) || deepClone(this.defaultConfig);
        this.revertFocusable();
        this.editMode = DynamicGridEditMode.Off;
    }

    protected handleKeyDown(event: KeyboardEvent): boolean {
        switch(getKey(event)) {
            case Key.Esc:
                this.cancelChanges();
                this.element.querySelector<HTMLElement>('.ess-dynamic-grid-context-menu-button')?.focus();
                break;
        }
        return true;
    }

    protected asGridArea(top: number, left: number, height: number, width: number) {
        return `${top || 'auto'} / ${left || 'auto'} / span ${height || DEFAULT_CELL_WIDTH} / span ${width || DEFAULT_CELL_WIDTH}`;
    }

    protected computeCellDimensions() {
        const { gridTemplateColumns, gridColumnGap, gridTemplateRows, gridRowGap, gridAutoRows } = getComputedStyle(this.element);
        const rowHeight = parseInt(gridAutoRows) || parseInt(gridTemplateRows);
        const colWidth = parseInt(gridTemplateColumns);
        this.rowGap = parseInt(gridRowGap);
        this.colGap = parseInt(gridColumnGap);
        this.dRow = rowHeight + this.rowGap;
        this.dCol = colWidth + this.colGap;
    }

    protected startResize(event: MouseEvent, index: number, direction: CompassFlags) {
        if(event.defaultPrevented || this.resize || event.button !== 0) {
            return;
        }

        const element = (event.target as HTMLElement).closest(DynamicGridSelector.ResizableContainer) as HTMLElement;

        this.resize = {
            h0: element.clientHeight,
            w0: element.clientWidth,
            x0: event.x,
            y0: event.y,
            hShift: 0,
            wShift: 0,
            xShift: 0,
            yShift: 0,
            resizeDirectionFlag: direction,
            element,
            componentIndex: index
        };

        // TODO [Matt] This likely isn't changing when we start a resize.
        // However, if it has changed since the last resize, we'll definitely
        // catch it this way.
        this.computeCellDimensions();
        element.style.height = `${element.clientHeight}px`;
        element.style.width = `${element.clientWidth}px`;

        document.addEventListener('mousemove', this.doResize);
        document.addEventListener('mouseup', this.endResize);
    }

    protected doResize = (event: MouseEvent) => {
        if(this.resize === null) {
            return;
        }
        const { x0, y0, hShift, wShift, resizeDirectionFlag, element, componentIndex } = this.resize;
        const { height, width } = this.gridConfig[componentIndex];
        let dx = event.x - x0;
        let dy = event.y - y0;
        let didResize = false;
        let offsetX = wShift * this.dCol;
        let offsetY = hShift * this.dRow;
        let translateX = 0;
        let translateY = 0;
        if(resizeDirectionFlag & CompassFlags.Up) {
            if(Math.abs(dy - offsetY) > this.dRow / 2) {
                const dir = Math.sign(dy - offsetY);
                if(height - dir > 0) {
                    this.resize.hShift -= dir;
                    this.resize.yShift += dir;
                    offsetY += dir * this.dRow;
                    didResize = true;
                }
            }
        }
        else if(resizeDirectionFlag & CompassFlags.Down) {
            if(Math.abs(dy - offsetY) > this.dRow / 2) {
                const dir = Math.sign(dy - offsetY);
                if(height + dir > 0) {
                    this.resize.hShift += dir;
                    offsetY += dir * this.dRow;
                    didResize = true;
                }
            }
        }
        if(resizeDirectionFlag & CompassFlags.Right) {
            if(Math.abs(dx - offsetX) > this.dCol / 2) {
                const dir = Math.sign(dx - offsetX);
                if(width + dir > 0) {
                    this.resize.wShift += dir;
                    offsetX += dir * this.dCol;
                    didResize = true;
                }
            }
        }
        else if(resizeDirectionFlag & CompassFlags.Left) {
            if(Math.abs(dx - offsetX) > this.dCol / 2) {
                const dir = Math.sign(dx - offsetX);
                if(width - dir > 0) {
                    this.resize.wShift -= dir;
                    this.resize.xShift += dir;
                    offsetX += dir * this.dCol;
                    didResize = true;
                }
            }
        }
        if(didResize && this.applyResize(this.resize)) {
            dx -= offsetX;
            dy -= offsetY;
        }
        if(resizeDirectionFlag & CompassFlags.Up) {
            element.style.height = `${Math.max(this.resize.h0 - dy, 0)}px`;
            translateY = Math.min(translateY + dy, this.resize.h0);
        }
        if(resizeDirectionFlag & CompassFlags.Right) {
            element.style.width = `${Math.max(this.resize.w0 + dx, 0)}px`;
        }
        if(resizeDirectionFlag & CompassFlags.Down) {
            element.style.height = `${Math.max(this.resize.h0 + dy, 0)}px`;
        }
        if(resizeDirectionFlag & CompassFlags.Left) {
            element.style.width = `${Math.max(this.resize.w0 - dx, 0)}px`;
            translateX += Math.min(translateX + dx, this.resize.w0);
        }
        element.style.transform = `translate(
            ${translateX}px,
            ${translateY}px
        )`;
    };

    protected endResize = (event: MouseEvent) => {
        if(this.resize) {
            const { componentIndex, element } = this.resize;
            const config = this.gridConfig[componentIndex];
            const height = config.height * this.dRow - this.rowGap;
            const width = config.width * this.dCol - this.colGap;
            animateOnce(element, {
                height: `${height - 2}px`,
                width: `${width - 2}px`,
                transform: 'translate(0, 0)'
            }).then(() => {
                element.style.height = 'auto';
                element.style.width = 'auto';
            });
        }
        this.resize = null;
        document.removeEventListener('mousemove', this.doResize);
        document.removeEventListener('mouseup', this.endResize);
    };

    protected applyResize(resize: IResizeContainer): boolean {
        const { wShift, hShift, xShift, yShift, componentIndex } = resize;
        let config = deepClone(this.gridConfig);
        let resizing = config[componentIndex];

        // resize self
        resizing.width += wShift!;
        resizing.height += hShift!;
        const top = resizing.top = resizing.top + yShift!;
        const left = resizing.left = resizing.left + xShift!;
        const bottom = resizing.top + resizing.height;
        const right = resizing.left + resizing.width!

        if(left < 1 || right > MAX_GRID_COLUMNS + 1 || top < 1 || bottom > this.rows + 1) {
            return false;
        }

        // find blockers
        const blocked = hasOverlappingCells(resizing, config);

        if(!blocked) {

            // update the origin
            resize.y0 += (resize.yShift || resize.hShift) * this.dRow;
            resize.x0 += (resize.xShift || resize.wShift) * this.dCol;
            resize.h0 += resize.hShift! * this.dRow;
            resize.w0 += resize.wShift! * this.dCol;

            // zero out the shift params
            resize.wShift = 0;
            resize.hShift = 0;
            resize.xShift = 0;
            resize.yShift = 0;

            // and update the config
            this.gridConfig.forEach((c, i) => Object.assign(c, config[i]));

            return true;
        }

        return false;
    }

    protected startMove(event: MouseEvent, index: number) {
        if(event.defaultPrevented || this.shift || event.button !== 0) {
            return;
        }
        const element = (event.target as HTMLElement).closest(DynamicGridSelector.ResizableContainer) as HTMLElement;
        element.style.cursor = 'grabbing';

        this.shift = {
            x0: event.x,
            y0: event.y,
            xShift: 0,
            yShift: 0,
            element,
            componentIndex: index,
        };

        this.computeCellDimensions();

        document.addEventListener('mousemove', this.doMove);
        document.addEventListener('mouseup', this.endMove);
    }

    protected doMove = (event: MouseEvent) => {
        if(this.shift !== null) {
            const { x0, y0, xShift, yShift, element } = this.shift;
            const dx = event.x - x0;
            const dy = event.y - y0;
            let didShift = false;
            let offsetX = xShift * this.dCol;
            let offsetY = yShift * this.dRow;
            if(Math.abs(dx - offsetX) > this.dCol / 2) {
                const dir = Math.sign(dx);
                this.shift.xShift += dir;
                offsetX += dir * this.dCol;
                didShift = true;
            }
            if(Math.abs(dy - offsetY) > this.dRow / 2) {
                const dir = Math.sign(dy);
                this.shift.yShift += dir;
                offsetY += dir * this.dRow;
                didShift = true;
            }
            if(didShift && this.applyShift(this.shift)) {
                element.style.transform = `translate(${dx - offsetX}px, ${dy - offsetY}px)`;
            } else {
                element.style.transform = `translate(${dx}px, ${dy}px)`;
            }
        }
    };

    protected endMove = (event: MouseEvent) => {
        if(this.shift !== null) {
            const { element } = this.shift;
            animateOnce(element, { transform: 'translate(0, 0)' });
            element.style.cursor = '';
        }
        this.shift = null;
        document.removeEventListener('mousemove', this.doMove);
        document.removeEventListener('mouseup', this.endMove);
    };

    protected applyShift(shift: IShiftContainer): boolean {
        const { xShift, yShift, componentIndex } = shift;
        let config = deepClone(this.gridConfig);
        let shifted: ICellConfig[] = [];

        const tryShift = (shifting: ICellConfig, dx: number, dy: number): boolean => {

            // dont shift any element more than once
            if(shifted.includes(shifting)) {
                return false;
            }

            shifted.push(shifting);

            // displace self
            const top = shifting.top = shifting.top + dy;
            const left = shifting.left = shifting.left + dx;
            const bottom = shifting.top + shifting.height;
            const right = shifting.left + shifting.width;

            if(left < 1 || right > MAX_GRID_COLUMNS + 1 || top < 1 || bottom > this.rows + 1) {
                return false;
            }

            // find displacers
            const displacers = findOverlappingCells(shifting, config);

            // displacer displacers
            return displacers.every((displacer) => tryShift(displacer, Math.sign(dx) * -shifting.width, Math.sign(dy) * -shifting.height));
        };

        // if shifting was successful
        if(tryShift(config[componentIndex], xShift!, yShift!)) {

            // update the origin
            shift.y0 += yShift * this.dRow;
            shift.x0 += xShift * this.dCol;

            // zero out the shift params
            shift.xShift = 0;
            shift.yShift = 0;

            // and update the config
            this.gridConfig.forEach((c, i) => Object.assign(c, config[i]));

            return true;
        }

        return false;
    }

    protected componentMatchesKey(key: string): (value: IComponentConfig) => boolean {
        return (component: IComponentConfig) => component.key === key;
    }

    /**
     * When in Edit mode, find all focusable elements inside the preview component and remove them from keyboard navigation
     */
    protected hideFocusable() {
        //
        this.focusableElementMap.clear();
        const focusableElements = Array.from<Element>(this.element.querySelectorAll('.ess-dynamic-grid-cell compose [tabindex]'));
        focusableElements.forEach((element) => {
            this.focusableElementMap.set(element, { tabindex: element.getAttribute('tabindex') });
            element.setAttribute('tabindex', "-1");
        });
    }

    /**
     * When exiting Edit mode, revert all focusable elements to their original keyboard navigation state
     */
    protected revertFocusable() {
        this.focusableElementMap.forEach((attributes, element) => {
            if(attributes.tabindex) {
                element.setAttribute('tabindex', attributes.tabindex);
            }
        });
    }

    static processContent(_compiler: any, _resources: any, node: Element, instruction: BehaviorInstruction): true {
        const fragment = document.createDocumentFragment();
        fragment.append(...node.children);
        COMPONENT_TEMPLATE_MAP.set(instruction, fragment);
        return true;
    }
}
